import Vector from "victor";
import makeTypeSafeCommitter from "../makeTypeSafeCommitter";
import { ModalType } from "@/store/ModalType";
import Rect from "@/wbcontroller/types/Rect";
import { getInitialModals } from "../initialStateHelpers";
import WhiteboardConfig from "@/WhiteboardConfig";

export interface ModalState {
  displayedModals: Set<ModalType>;
  modalAnchors: Partial<Record<ModalType, Rect>>;
}

const mutations = {
  openModal(state: ModalState, m: ModalType) {
    state.displayedModals.add(m);
    state.displayedModals = new Set(state.displayedModals);
  },
  closeModals(state: ModalState) {
    state.displayedModals.clear();
    state.displayedModals = new Set(state.displayedModals);
  },
  closeModal(state: ModalState, modalType: ModalType) {
    state.displayedModals.delete(modalType);
  },
  updateModalAnchor(
    state: ModalState,
    payload: {
      modalType: ModalType;
      selector?: string;
    }
  ) {
    if (!payload.selector) {
      delete state.modalAnchors[payload.modalType];
      return;
    }
    const el = document.querySelector(payload.selector);
    if (el) {
      const rect = el.getBoundingClientRect();
      state.modalAnchors[payload.modalType] = new Rect(
        new Vector(rect.left, rect.top),
        new Vector(rect.right, rect.bottom)
      );
    } else {
      delete state.modalAnchors[payload.modalType];
    }
  },
};
type ModalMutationsWithState = typeof mutations;

export const ModalMutations = makeTypeSafeCommitter<
  ModalState,
  typeof mutations
>(mutations);

const getters = {};
type ModalGettersWithState = typeof getters;
export type ModalGetters = {
  [Property in keyof ModalGettersWithState]: ReturnType<
    ModalGettersWithState[Property]
  >;
};

export function makeModalModule(config: WhiteboardConfig): {
  state: () => ModalState;
  mutations: ModalMutationsWithState;
  getters: ModalGettersWithState;
} {
  return {
    state: () => ({
      displayedModals: getInitialModals(config),
      modalAnchors: {},
    }),
    mutations,
    getters,
  };
}
